<template>
    <div
        class="position-relative d-inline-flex align-middle"
        @mouseover="showTooltip"
        @mouseleave="hideTooltip"
        @focus="showTooltipFocus"
        @blur="hideTooltipBlur"
        @click="toggleTooltipClick"
    >
        <slot name="icon">
            <i class="far fa-info-circle text-info cursor-pointer"></i>
        </slot>
        <div v-if="show" class="tooltip show vue-tooltip" :class="'tooltip-' + position" :id="id">
            <div class="vue-tooltip-arrow arrow"></div>
            <div class="tooltip-inner">
                <slot name="tooltip-content"></slot>
            </div>
        </div>
    </div>
</template>

<script>
import GenerateId from '../mixins/GenerateId';
import { testIsArray } from '../../../../../lib/test-and-assert/test-base';

export default {
    name: 'Tooltip',
    mixins: [GenerateId],
    props: {
        position: {
            type: String,
            default: 'right',
            validator: function (val) {
                const validPositions = ['top', 'right', 'bottom'];

                return validPositions.includes(val);
            },
        },
    },
    beforeMount() {
        this.id = 'vuetooltip-' + this.generateId();

        document.addEventListener('click', this.onDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onDocumentClick);
    },
    methods: {
        onDocumentClick(event) {
            if (this.clicked) {
                this.clicked = false;

                return;
            }

            this.hideTooltipBlur(event);
        },
        toggleTooltipClick(event) {
            // ignore the next document click event to prevent the tooltip from immediately closing
            this.clicked = true;

            if (this.show && this.focused) {
                this.hideTooltipBlur(event);

                return;
            }

            this.showTooltipFocus();
        },
        showTooltip() {
            this.show = true;
        },
        showTooltipFocus() {
            this.focused = true;
            this.showTooltip();
        },
        hideTooltip() {
            if (this.focused) {
                return;
            }

            this.show = false;
        },
        hideTooltipBlur(event) {
            const eventPath = event?.composedPath() ?? event?.path ?? null;
            // check if event.path is array & at least one entry in path has the same id as the current tooltip
            const hasCurrentToolTipInPath = testIsArray(eventPath) && eventPath.some((el) => el?.id === this.id);

            // do not hide tooltip if user clicked on current tooltip,
            // but hide tooltip if user clicked on another tooltip
            if (hasCurrentToolTipInPath) {
                event.stopPropagation();
                event.preventDefault();

                return;
            }

            this.focused = false;
            this.hideTooltip();
        },
    },
    data() {
        return {
            show: false,
            focused: false,
            id: null,
            clicked: false,
        };
    },
};
</script>

<style lang="scss">
.vue-tooltip-arrow.arrow {
    display: block !important;
}

.tooltip {
    user-select: none;

    .tooltip-inner {
        width: max-content;
    }

    .arrow::before {
        border-width: 0.4rem;
        border-color: transparent;
    }
}

.tooltip.tooltip-right {
    left: calc(100% + 5px);
    top: -0.5em;

    .arrow {
        top: 0.5em;
        right: 100%;

        &::before {
            right: 0;
            border-left-width: 0;
            border-right-color: #161616;
        }
    }
}

.tooltip.tooltip-top {
    bottom: calc(100% + 5px);
    right: -0.5em;

    .arrow {
        right: 0.6em;
        top: 100%;

        &::before {
            right: 0;
            border-bottom-width: 0;
            border-top-color: #161616;
        }
    }
}

.tooltip.tooltip-bottom {
    top: calc(100% + 5px);
    right: -0.5em;

    .arrow {
        right: 0.6em;
        bottom: 100%;

        &::before {
            right: 0;
            border-top-width: 0;
            border-bottom-color: #161616;
        }
    }
}
</style>
